import { create } from 'zustand';
import { useEffect } from 'react';

interface PendingEdit {
  type: 'task' | 'note';
  id: string;
}

interface NotepadDrawerStore {
  isOpen: boolean;
  pendingEdit: PendingEdit | null;
  open: () => void;
  close: () => void;
  toggle: () => void;
  openWithTask: (taskId: string) => void;
  clearPendingEdit: () => void;
}

/**
 * Global notepad drawer state
 * Supports opening with a specific task via openWithTask()
 */
export const useNotepadDrawerStore = create<NotepadDrawerStore>((set) => ({
  isOpen: false,
  pendingEdit: null,
  open: () => set({ isOpen: true }),
  close: () => set({ isOpen: false, pendingEdit: null }),
  toggle: () => set((state) => ({ isOpen: !state.isOpen })),
  openWithTask: (taskId: string) =>
    set({
      isOpen: true,
      pendingEdit: { type: 'task', id: taskId },
    }),
  clearPendingEdit: () => set({ pendingEdit: null }),
}));

/**
 * Hook to control notepad drawer and register global hotkey (Cmd/Ctrl+J)
 *
 * @example
 * const { isOpen, open, close } = useNotepadDrawer();
 */
export function useNotepadDrawer() {
  const { isOpen, open, close, toggle, pendingEdit, openWithTask, clearPendingEdit } =
    useNotepadDrawerStore();

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Cmd+J (Mac) or Ctrl+J (Windows/Linux)
      if ((e.metaKey || e.ctrlKey) && e.key === 'j') {
        e.preventDefault();
        toggle();
      }

      // ESC to close (handled by Sheet component, but included for completeness)
      if (e.key === 'Escape' && isOpen) {
        close();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, toggle, close]);

  return { isOpen, open, close, toggle, pendingEdit, openWithTask, clearPendingEdit };
}
